import React from 'react';
import Card from '../design-system/Card';
import { colors, spacing, fontStack } from '../design-system/tokens';

/**
 * MetricCard — small surface for a single labelled metric.
 *
 * Props:
 *   - label    : string  (small caps top-left)
 *   - value    : string | number  (big number)
 *   - subtitle : string?  (descriptive line below value)
 *   - accent   : string?  (optional accent color → left border bar)
 *   - trend    : 'up' | 'down' | 'flat'?  (optional trend indicator)
 *   - onClick  : ()=>void?
 */
export default function MetricCard({
  label,
  value,
  subtitle,
  accent,
  trend,
  onClick,
}) {
  return (
    <Card accentColor={accent} onClick={onClick} padding={spacing.lg}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          fontFamily: fontStack,
          minHeight: 96,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: colors.textSecondary,
              fontWeight: 600,
            }}
          >
            {label}
          </div>
          {trend && <TrendIndicator trend={trend} />}
        </div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 600,
            color: colors.textPrimary,
            letterSpacing: '-0.01em',
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 13,
              color: colors.textSecondary,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
    </Card>
  );
}

function TrendIndicator({ trend }) {
  const map = {
    up:   { glyph: '▲', color: colors.accentEnergy },
    down: { glyph: '▼', color: '#E07A5F' },
    flat: { glyph: '—', color: colors.textSecondary },
  };
  const { glyph, color } = map[trend] || map.flat;
  return (
    <span
      aria-label={`Trend ${trend}`}
      style={{
        fontSize: 11,
        color,
        fontWeight: 600,
      }}
    >
      {glyph}
    </span>
  );
}
